import React, { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import clsx from 'clsx';

import { NavBar } from 'components/NavBar';

import cog from 'assets/images/settings.svg';
import chevron from 'assets/images/chevron-down.svg';

import NotificationsCenter from 'components/NotificationsCenter';
import './style.scss';

const AppBar = () => {
  const history = useHistory();
  const userInfo = useSelector((state) => state.global.userInfo);
  const organization = useSelector((state) => state.global.userInfo.organization);
  const personas = useSelector((state) => state.global.userInfo.personas);
  const { orgId } = userInfo;
  const { isPro } = personas || {};

  const { isPaying } = organization || {};

  const { isMasquerading, isSettings, integrationsEnabled: isIntegrationsEnabled } = userInfo;
  const name = `${userInfo.firstName} ${userInfo.lastName}`;

  const { isPaused, isDisabled, isCancel, billing } = userInfo || {};
  const { planningSubscription: subscription } = billing || {};

  const isAccountCanceled =
    isCancel ||
    // forcibly suspended
    (!subscription && ((isCancel && userInfo?.cancelAt)) || (subscription?.isEnded && subscription?.endedAt));

  const showNotifications = !isPaused && !isDisabled && !isCancel;

  const [isOpen, setIsOpen] = useState(false);

  const settingsPath = () => {
    // If the user lacks settings view/edit access permissions but has integrations enabled,
    // they should still have access to the 'Integrations' tab in the settings page.
    // Redirect them to the 'Integrations' settings tab  when clicking the cog wheel.
    if (!isSettings && isIntegrationsEnabled) {
      return `/app/v2/pro/${orgId}/settings/integrations`;
    }

    const perms = userInfo.permissions;
    if (orgId) {
      // If account is disabled, go straight to members.
      if (isDisabled) {
        return `/app/pro/${orgId}/settings/members`;
      }
      if (!perms['settings.org.view']) {
        let setting = '';
        if (perms['settings.branding.view']) {
          setting = 'branding';
        }
        else if (perms['settings.lead.view']) {
          setting = 'leads';
        }
        else if (perms['settings.template.view']) {
          setting = 'templates';
        }

        return `/app/pro/${orgId}/settings/${setting}`;
      }

      return `/app/pro/${orgId}/settings`;
    }
    return '/app';
  };
  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  const renderUserMenu = () => (
    <Dropdown
      firstName={userInfo?.firstName}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <DropdownItem active={useRouteMatch('/app/v2/account')}
        onClick={(e) => {
          e.preventDefault();
          history.push('/app/v2/account');
          handleMenuItemClick();
        }}
      >
        My Account
      </DropdownItem>
      {isPaying && !isPaused ? (
        <DropdownItem active={false}
          href="/app/account/refer-a-friend"
        >
          Refer a friend
        </DropdownItem>
      ) : null}
      {isPro && (
        <>
          <div className="dropdown-divider user-menu__divider" />
          {orgId ? (
            <>
              <DropdownItem active={false}
                href="http://help.aisleplanner.com/getting-started"
                target="_blank"
                onClick={handleMenuItemClick}
              >
                Getting started
              </DropdownItem>
              <DropdownItem active={false}
                href="https://help.aisleplanner.com/en/articles/1899421-welcome-to-aisle-planner"
                target="_blank"
                onClick={handleMenuItemClick}
              >
                On demand demo
              </DropdownItem>
            </>
          ) : null}
          <DropdownItem active={false}
            target="_blank"
            href="https://help.aisleplanner.com/"
            onClick={handleMenuItemClick}
          >
            Help center
          </DropdownItem>
          <DropdownItem active={false}
            target="_blank"
            href="https://pro.aisleplanner.com/blog"
            onClick={handleMenuItemClick}
          >
            Pro Blog
          </DropdownItem>
          <DropdownItem active={false}
            target="_blank"
            href="https://www.aisleplanner.com/ap-education"
            onClick={handleMenuItemClick}
          >
            AP Education
          </DropdownItem>
          <DropdownItem active={false}
            target="_blank"
            href="https://pro.aisleplanner.com/industry-events"
            onClick={handleMenuItemClick}
          >
            Industry Events
          </DropdownItem>
          <DropdownItem active={false}
            target="_blank"
            href="https://pro.aisleplanner.com/pro-partners"
            onClick={handleMenuItemClick}
          >
            Pro Partners
          </DropdownItem>
          <div className="dropdown-divider user-menu__divider" />
        </>
      )}
      {/* <DropdownItem href="/app/faves">My Faves</DropdownItem> */}
      {userInfo.admin && (
        <DropdownItem active={false}
          href="/adminapp"
          onClick={handleMenuItemClick}
        >
          Admin Portal
        </DropdownItem>
      )}
      <DropdownItem active={false}
        href="/signout"
      >
        Log out
      </DropdownItem>
      {isMasquerading && (
        <DropdownItem active={false}
          onClick={() => {

            if (window.Intercom) {
              window.Intercom('shutdown');
            }
            handleMenuItemClick();
          }}
          href="/adminapp-masquerade/drop"
        >
          Return to admin
        </DropdownItem>
      )}
    </Dropdown>
  );

  return (
    <section className="header-container">
      <div
        className={clsx('color-navbar', 'navbar-dark', isMasquerading && 'is-masquerading')}
        data-title={isMasquerading ? `Viewing as ${name}` : false}
        id="navbar"
      >
        <div className="navbar-list navbar-nav">
          <div className="navbar-item color-appbar">
            <div className="navbar-nav">
              <NavBar />
            </div>
            {showNotifications && (
              <div className="navbar-item navbar-dropdown-item navbar-item--notifications">
                <span
                  className="nav-notification"
                >
                  <NotificationsCenter />
                </span>
              </div>
            )}
            { renderUserMenu() }
            {(isSettings || isIntegrationsEnabled) && !isPaused && !isAccountCanceled && (
              <div className="navbar-title navbar-item navbar-dropdown-item">
                <a
                  href={settingsPath()}
                  className="nav-link appbar-option appbar-settings"
                >
                  <img
                    src={cog}
                    alt="settings"
                  />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Dropdown = ({ firstName = '', isOpen, setIsOpen, children }) => {
  // If we need to disable the dropdown for whatever reason.
  const disabled = false;
  const useDismissProps = { referencePressEvent: 'click', bubbles: false };
  const flipOptions = {
    fallbackPlacements: ['top', 'bottom'],
  };


  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const show = () => {
    setIsOpen(true);
  };

  const dismiss = () => {
    setIsOpen(false);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: 'bottom-end',
    middleware: [
      offset(0),
      flip(flipOptions),
    ],
    onOpenChange(opened, event) {
      if (!disabled && opened) {
        show(event);
      } else if (dismiss) {
        dismiss(event);
      }
    },
    whileElementsMounted: autoUpdate,
  });

  const close = useDismiss(context, useDismissProps);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    close,
  ]);

  return (
    <div
      ref={refs.setReference}
      className="ap-mobile-menu ap-menu navbar-item navbar-dropdown-item"
      {...getReferenceProps()}
    >
      <button
        className="appbar-option nav-link dropdown-toggle button--unstyled"
        onClick={toggleDropdown}
      >
        {firstName}
        <img
          src={chevron}
          className={clsx('dropdown-icon', isOpen && 'dropdown-icon--open')}
        />
      </button>
      {isOpen && (
        <div
          ref={refs.setFloating}
          className="dropdown-menu dropdown-menu-right user-dropdown"
          style={{ ...floatingStyles }}
          {...getFloatingProps()}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ href, onClick, target, active, children }) => {
  if (!href) {
    return (
      <a
        href="#"
        onClick={onClick}
        className={clsx('dropdown-item', active && 'active')}
      >
        {children}
      </a>
    );
  }
  return (
    <a
      target={target}
      href={href}
      onClick={onClick}
      className={clsx('dropdown-item', active && 'active')}
    >
      {children}
    </a>
  );
};

export default AppBar;
